import { type Camera, type Scene, type WebGLRenderer, WebGLRenderTarget } from '@seddi/three';
import { RenderPass } from '@seddi/three/addons/postprocessing/RenderPass.js';

/**
 * We want MSAA on the geometry render but subsequent passes operating on a resolved non-multisampled texture.
 * The composer's simple design makes this impossible, but this is a clean solution.
 */
export class MsaaRenderPass extends RenderPass {
  private msaaBuffer: WebGLRenderTarget | null;
  private samples: number;

  constructor(scene: Scene, camera: Camera, samples = 4) {
    super(scene, camera);
    this.msaaBuffer = null;
    this.samples = samples;
  }

  dispose() {
    super.dispose();
    this.msaaBuffer?.dispose();
  }

  /**
   * Sets the size of the pass.
   * @param width - Width in device pixel content box size units.
   * @param height - Height in device pixel content box size units.
   */
  setSize(width: number, height: number) {
    super.setSize(width, height);

    if (this.msaaBuffer) {
      this.msaaBuffer.setSize(width, height);
    }
  }

  render(
    renderer: WebGLRenderer,
    writeBuffer: WebGLRenderTarget,
    readBuffer: WebGLRenderTarget,
    deltaTime: number,
    maskActive: boolean,
  ) {
    // GOTCHA: When RenderPass is used, it is usually the first pass in the pass chain. Hence, it’s possible to directly write in the read buffer and safe the swap and the corresponding overhead.
    // https://discourse.threejs.org/t/renderpass-appears-to-write-to-the-readbuffer/28282

    // Lazily create the MSAA target matching the composer buffer format
    if (!this.msaaBuffer) {
      if (readBuffer.depthTexture) {
        // TODO: Fill a depthTexture if we want to resolve the depth too
      }
      this.msaaBuffer = new WebGLRenderTarget(readBuffer.width, readBuffer.height, {
        samples: this.samples,
        type: readBuffer.texture.type,
      });
    }

    const { msaaBuffer } = this;

    // Render to our internal MSAA target
    // Already resolves multisample renderbuffers to a single-sample texture
    super.render(renderer, writeBuffer, msaaBuffer, deltaTime, maskActive);

    // Copy result to the composer buffer, requires manual lazy initialization
    renderer.setRenderTarget(readBuffer);
    renderer.copyTextureToTexture(msaaBuffer.texture, readBuffer.texture);

    if (readBuffer.depthTexture) {
      renderer.copyTextureToTexture(msaaBuffer.depthTexture, readBuffer.depthTexture);
    }
  }
}
